import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  createLead,
  getLeads,
  updateLead,
  acceptLeadAndCreateProject,
  getAllClientCompanies,
  getClientContacts,
  deleteLead,
  exportLeads,
  getLeadActions,
  createLeadAction,
  reorderLeadActions,
  getLeadAssignments,
  assignEmployeesToLead,
} from "../../services/leadService";
import { getEmployees } from "../../services/peopleService";
import { setLeadLabels } from "../../services/labelService";
import { usePermissions } from "../../hooks/usePermission";
import { useCurrentUser } from "../../context/UserContext";
import { useCurrency } from "../../hooks/useCurrency";
import { 
  LEAD_STATUSES, 
  ACTIVE_STATUSES,
  CLOSED_STATUSES,
  WON_STATUS, 
  isClosedStatus, 
  getStatusColor
} from "../../constants/leadStatuses";
import ui from "./DashboardUI.module.css";
import FormModal from "../../components/forms/FormModal";
import DetailsModal from "../../components/forms/DetailsModal";
import detailStyles from "../../components/forms/DetailsModal.module.css";
import SettingsToggle from "../../components/forms/SettingsToggle";
import DataTable from "../../components/forms/DataTable";
import LabelSelector from "../../components/forms/LabelSelector";
import CustomDropdown from "../../components/forms/CustomDropdown";
import AddStatusModal from "../../components/AddStatusModal";
import ConfirmDialog from "../../components/ConfirmDialog";
import settingsIcon from "../../assets/icons/settings.svg";
import plusIcon from "../../assets/icons/plus.svg";
import Toast from "../../components/Toast";
import useMinimumLoader from "../../hooks/useMinimumLoader";
import ReorderListModal from "../../components/ReorderListModal";
import KanbanView from "../../components/projects/KanbanView";
import kanban from "../../components/projects/KanbanView.module.css";
import { getInitials, avatarColorFromName } from "../../utils/avatarUtils";
import EditableField from "../../components/common/EditableField";
import ActivityPanel from "../../components/common/ActivityPanel";
import Icon from "../../components/common/Icon";
import { FILE_UPLOADS_ENABLED, UPLOADS_DISABLED_MESSAGE } from "../../config/features";
import Avatar from "../../components/common/Avatar";
import * as activityService from "../../services/activityService";
import { getCustomFields } from "../../services/customFieldService";
import CustomFieldModal from "../../components/settings/CustomFieldModal";
import {
  columnKeyFor,
  isCustomColumnKey,
  fieldForColumnKey,
  getFieldValue,
  renderCustomValue,
  compareCustomValues,
  mergeCustomColumns,
  loadColumnVisibility,
  saveColumnVisibility,
  pruneColumnVisibility,
} from "../../utils/customFieldUtils";
import CustomFieldRows from "../../components/common/CustomFieldRows";

const COLUMN_STORAGE_KEY = "leadsColumns";

const BASE_LEAD_COLUMNS = [
  { key: "client_company", label: "Company", visible: true },
  { key: "project_name", label: "Project", visible: true },
  { key: "client_name", label: "Name", visible: false },
  { key: "labels", label: "Labels", visible: true },
  { key: "email", label: "Email", visible: false },
  { key: "phone_number", label: "Phone", visible: true },
  { key: "assignees", label: "Team", visible: true },
  { key: "budget_cost", label: "Budget", visible: false },
  { key: "location", label: "Location", visible: false },
  { key: "address", label: "Address", visible: false },
  { key: "status", label: "Status", visible: true },
  { key: "action", label: "Action", visible: true },
];

export default function Leads() {
  // Permission checks
  const {
    canViewLeads,
    canViewAssignedLeads,
    canCreateLeads,
    canEditLeads,
    canDeleteLeads,
    canManageCompanySettings,
    canManageLeadTeam,
  } = usePermissions();

  const currentUser = useCurrentUser();
  const { format: formatMoney, symbol: currencySymbol } = useCurrency();

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const showLoading = useMinimumLoader(loading);
  const [showForm, setShowForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(
    Object.fromEntries(LEAD_STATUSES.map(item => [item, false]))
  );

  // Lead actions state
  const [leadActions, setLeadActions] = useState([]);
  const [showAddActionModal, setShowAddActionModal] = useState(false);
  const [addActionContext, setAddActionContext] = useState(null); // 'table', 'create', 'edit'
  const [showActionReorderModal, setShowActionReorderModal] = useState(false);
  const [savingActionOrder, setSavingActionOrder] = useState(false);

  // Status confirmation state (for Won/Lost)
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [statusConfirmMessage, setStatusConfirmMessage] = useState('');
  const [statusConfirmButton, setStatusConfirmButton] = useState('Confirm');
  const [pendingStatusAction, setPendingStatusAction] = useState(null);

  // Search, sort, filter, group state
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [filterLabel, setFilterLabel] = useState("");
  const [groupBy, setGroupBy] = useState("status");
  const [showArchive, setShowArchive] = useState(false);
  const [colorDotMode, setColorDotMode] = useState("status"); // 'action' or 'status'
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('leadsViewMode') || 'table'; } catch { return 'table'; }
  });
  useEffect(() => {
    try { localStorage.setItem('leadsViewMode', viewMode); } catch {}
  }, [viewMode]);
  const [kanbanGroupBy, setKanbanGroupBy] = useState(() => {
    try { return localStorage.getItem('leadsKanbanGroupBy') || 'status'; } catch { return 'status'; }
  });
  useEffect(() => {
    try { localStorage.setItem('leadsKanbanGroupBy', kanbanGroupBy); } catch {}
  }, [kanbanGroupBy]);

  const [editableLead, setEditableLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [leadFiles, setLeadFiles] = useState([]);
  const [uploadingLeadFile, setUploadingLeadFile] = useState(false);
  const leadFileInputRef = useRef(null);
  const [teamSearchFocused, setTeamSearchFocused] = useState(false);
  const [editingLabels, setEditingLabels] = useState(false);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);

  // Team assignment state
  const [assignedEmployees, setAssignedEmployees] = useState([]);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [, setTeamDirty] = useState(false);
  const settingsRef = useRef();

  const [toast, setToast] = useState(null);

  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [allClientCompanies, setAllClientCompanies] = useState([]);
  const [clientContacts, setClientContacts] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showNameDropdown, setShowNameDropdown] = useState(false);

  // Form state
  const [client_name, setName] = useState("");
  const [client_company, setCompany] = useState("");
  const [project_name, setProject] = useState("");
  const [email, setEmail] = useState("");
  const [phone_number, setPhone] = useState("");
  const [description, setDescription] = useState("");
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [actionId, setActionId] = useState("");
  const [budget_cost, setBudgetCost] = useState("");
  const [budget_hours, setBudgetHours] = useState("");
  const [location, setLocation] = useState("");
  const [address, setAddress] = useState("");
  const [activeTab, setActiveTab] = useState("details"); // details or budget

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportForm, setExportForm] = useState({
    status: "",
    includeLabels: true,
    includeBudget: true,
    includeCustomFields: true,
    groupBy: "", // empty = no grouping, "company" = client company, "contact" = client name
  });

  // Edit modal labels state
  const [editLabels, setEditLabels] = useState([]);

  // Per-company custom fields applied to leads, defined in Settings or added
  // straight from the "+" at the end of the table header.
  const [customFields, setCustomFields] = useState([]);
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);

  // Only ever follows a create from the table's "+" button, so the new field
  // has no values by definition and the loaded rows are still correct.
  // getFieldValue already treats a missing key as empty, so there is nothing to
  // patch into them — refetching the whole (unpaginated) list here would be the
  // most expensive request in the app fired for no new data.
  const refreshCustomFields = async () => {
    const data = await getCustomFields('lead');
    setCustomFields(data.fields || []);
  };

  // Column visibility state. Choices are remembered per user; a column the user
  // has never seen (a newly created custom field) falls back to its default.
  const [columns, setColumns] = useState(() => {
    const saved = loadColumnVisibility(COLUMN_STORAGE_KEY);
    return BASE_LEAD_COLUMNS.map((col) => (
      Object.prototype.hasOwnProperty.call(saved, col.key)
        ? { ...col, visible: saved[col.key] }
        : col
    ));
  });

  // Custom field columns can only be merged in once the definitions arrive, so
  // this can't live in the useState initialiser above. Pruning happens here for
  // the same reason: only now is it known which custom fields still exist.
  useEffect(() => {
    if (customFields.length > 0) pruneColumnVisibility(COLUMN_STORAGE_KEY, customFields);
    setColumns((prev) => mergeCustomColumns(
      prev.length > 0 ? prev : BASE_LEAD_COLUMNS,
      customFields,
      loadColumnVisibility(COLUMN_STORAGE_KEY)
    ));
  }, [customFields]);

  useEffect(() => {
    saveColumnVisibility(COLUMN_STORAGE_KEY, columns);
  }, [columns]);

  const toggleColumn = (key) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.key === key ? { ...col, visible: !col.visible } : col
      )
    );
  };

  // One cell renderer per custom field, merged into DataTable's static map.
  const customCellRenderers = useMemo(() => Object.fromEntries(
    customFields.map((field) => [
      columnKeyFor(field),
      (row) => renderCustomValue(field, getFieldValue(row, field), formatMoney) ?? "—",
    ])
  ), [customFields, formatMoney]);

  const toggleGroupCollapse = (status) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [status]: !prev[status],
    }));
  };

  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnKey);
      setSortDirection("asc");
    }
  };

  // Compute filtered and sorted lead list
  const getDisplayedLeads = () => {
    let result = showArchive
      ? leads.filter((l) => isClosedStatus(l.status))
      : leads.filter((l) => !isClosedStatus(l.status));

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (l) =>
          (l.project_name || "").toLowerCase().includes(term) ||
          (l.client_company || "").toLowerCase().includes(term) ||
          (l.client_name || "").toLowerCase().includes(term)
      );
    }

    // Status filter
    if (filterStatus) {
      result = result.filter((l) => l.status === filterStatus);
    }

    // Action filter
    if (filterAction) {
      result = result.filter((l) => String(l.action_id) === String(filterAction));
    }

    // Label filter
    if (filterLabel) {
      result = result.filter((l) =>
        l.labels && l.labels.some((lb) => String(lb.label_id) === filterLabel)
      );
    }

    // Sorting
    if (sortColumn) {
      // Custom fields carry their own type, so they sort by it rather than
      // falling through to the string comparison below.
      const customField = isCustomColumnKey(sortColumn)
        ? fieldForColumnKey(customFields, sortColumn)
        : null;

      result = [...result].sort((a, b) => {
        if (customField) {
          const comparison = compareCustomValues(
            customField,
            getFieldValue(a, customField),
            getFieldValue(b, customField)
          );
          return sortDirection === "asc" ? comparison : -comparison;
        }

        let valA = a[sortColumn];
        let valB = b[sortColumn];

        if (sortColumn === "budget_cost") {
          valA = parseFloat(valA) || 0;
          valB = parseFloat(valB) || 0;
        } else {
          valA = (valA || "").toString().toLowerCase();
          valB = (valB || "").toString().toLowerCase();
        }

        if (valA < valB) return sortDirection === "asc" ? -1 : 1;
        if (valA > valB) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  };

  const displayedLeads = getDisplayedLeads();

  // Add computed display_color based on colorDotMode
  const displayedLeadsWithColor = useMemo(() => {
    return displayedLeads.map(lead => ({
      ...lead,
      display_color: colorDotMode === 'status' 
        ? getStatusColor(lead.status)
        : lead.action_color
    }));
  }, [displayedLeads, colorDotMode]);

  const archivedCount = useMemo(() => leads.filter((l) => isClosedStatus(l.status)).length, [leads]);

  const availableLabels = useMemo(() => {
    const labelMap = new Map();
    leads.forEach((l) => {
      (l.labels || []).forEach((lb) => {
        if (!labelMap.has(lb.label_id)) labelMap.set(lb.label_id, lb.name);
      });
    });
    return Array.from(labelMap.entries());
  }, [leads]);

  const hasActiveFilters = !!(searchTerm || filterStatus || filterAction || filterLabel);

  const resetView = () => {
    setSearchTerm("");
    setFilterStatus("");
    setFilterAction("");
    setFilterLabel("");
    setSortColumn(null);
    setSortDirection("asc");
    setGroupBy("status");
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leadsData, companiesData, actionsData, employeesData, customFieldsData] = await Promise.all([
          getLeads(),
          getAllClientCompanies(),
          getLeadActions(),
          getEmployees(),
          getCustomFields('lead'),
        ]);

        setLeads(leadsData.leads);
        setAllClientCompanies(companiesData);
        setLeadActions(actionsData.actions || []);
        setCustomFields(customFieldsData.fields || []);
        setAvailableEmployees((employeesData.employees || employeesData || []).map(e => ({
          id: e.user_id,
          name: e.name,
          job_title: e.job_title,
        })));
        setLoading(false);
      } catch (err) {
        console.error(err);
        setToast({ message: "Failed to load leads.", type: "error" });
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setShowSettings(false);
      }
    };

    if (showSettings) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSettings]);

  const handleCompanyFocus = (isEdit = false) => {
    if (isEdit) {
      setShowCompanyDropdown(true);
      setCompanySuggestions(allClientCompanies.map(c => c.company_name));
    } else {
      setShowCompanyDropdown(true);
      setCompanySuggestions(allClientCompanies.map(c => c.company_name));
    }
  };

  const handleCompanyInput = (val, isEdit = false) => {
    if (isEdit) {
      setEditableLead({ ...editableLead, client_company: val });
    } else {
      setCompany(val);
    }
    
    if (val.trim() === '') {
      setCompanySuggestions(allClientCompanies.map(c => c.company_name));
    } else {
      // Check if there's an exact match
      const exactMatch = allClientCompanies.find(c => 
        c.company_name.toLowerCase() === val.toLowerCase()
      );
      
      if (exactMatch) {
        // If exact match exists, only show that
        setCompanySuggestions([exactMatch.company_name]);
      } else {
        // Otherwise, show all that start with the query
        const filtered = allClientCompanies
          .filter(c => c.company_name.toLowerCase().startsWith(val.toLowerCase()))
          .map(c => c.company_name);
        setCompanySuggestions(filtered);
      }
    }
  };

  const handleCompanySelect = (companyName, isEdit = false) => {
    if (isEdit) {
      setEditableLead({ ...editableLead, client_company: companyName });
    } else {
      setCompany(companyName);
    }
    setCompanySuggestions([]);
    setShowCompanyDropdown(false);
    // When company is selected, load contacts for that company
    loadContactsForCompany(companyName);
  };

  const loadContactsForCompany = async (companyName) => {
    if (!companyName) {
      setClientContacts([]);
      return;
    }
    const company = allClientCompanies.find(c => c.company_name === companyName);
    if (company) {
      const contacts = await getClientContacts(company.client_company_id);
      setClientContacts(contacts);
    }
  };

  const openLeadDetail = async (lead) => {
    setEditableLead({ ...lead });
    if (lead.client_company) {
      await loadContactsForCompany(lead.client_company);
    }
    setEditLabels(lead.labels || []);
    try {
      const assignments = await getLeadAssignments(lead.lead_id);
      setAssignedEmployees(assignments.map(a => ({
        id: a.employee_id,
        name: a.user_name,
        job_title: a.job_title || '',
        role: '',
      })));
    } catch {
      setAssignedEmployees([]);
    }
    setTeamDirty(false);
    try {
      const files = await activityService.listFiles('lead', lead.lead_id);
      setLeadFiles(files || []);
    } catch (err) {
      console.error("Failed to fetch lead files:", err);
      setLeadFiles([]);
    }
    setShowDetailModal(true);
  };

  const persistLeadTeamAssignments = async (updatedList) => {
    try {
      await assignEmployeesToLead(editableLead.lead_id, {
        assignedBy: currentUser.user_id,
        employees: updatedList.map((emp) => ({
          user_id: emp.id,
          role: emp.role,
        })),
      });
      setAssignedEmployees(updatedList);
    } catch (err) {
      console.error(err);
      setToast({ message: err.message || "Failed to update team", type: "error" });
    }
  };

  const handleAddLeadTeamMember = (emp) => {
    setEmployeeSearch("");
    persistLeadTeamAssignments([...assignedEmployees, { ...emp, role: "" }]);
  };

  const handleRemoveLeadTeamMember = (empId) => {
    persistLeadTeamAssignments(assignedEmployees.filter((e) => e.id !== empId));
  };

  const updateLeadAndRefresh = async (id, fields) => {
    const result = await updateLead(id, fields);
    setActivityRefreshKey(k => k + 1);
    return result;
  };

  // Files tab upload. The POST response has no uploaded_by_name (that column
  // comes from a JOIN in the list query), so refetch rather than prepending the
  // response — otherwise the new row reads "Unknown" until the modal reopens.
  const handleLeadFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editableLead?.lead_id) return;

    setUploadingLeadFile(true);
    try {
      await activityService.uploadFile('lead', editableLead.lead_id, file);
      const files = await activityService.listFiles('lead', editableLead.lead_id);
      setLeadFiles(files || []);
      setActivityRefreshKey((k) => k + 1);
      setToast({ message: "File uploaded", type: "success" });
    } catch (err) {
      console.error("Failed to upload file:", err);
      setToast({ message: err.message || "Failed to upload file", type: "error" });
    } finally {
      setUploadingLeadFile(false);
    }
  };

  const handleLeadFileDelete = async (file) => {
    try {
      await activityService.deleteFile('lead', editableLead.lead_id, file.file_id);
      setLeadFiles((prev) => prev.filter((f) => f.file_id !== file.file_id));
      setActivityRefreshKey((k) => k + 1);
    } catch (err) {
      setToast({ message: err.message || "Failed to delete file", type: "error" });
    }
  };

  // Status and action read as the same pill wherever they appear — in the
  // title band and in the Details property list — so neither spot looks like
  // the "real" one.
  // Tinted with the record's own colour (hex + alpha) rather than a flat grey,
  // so a pill carries the same meaning it does in the board and table views.
  const renderLeadStatusPill = (status) => {
    const value = status || 'Open';
    const color = getStatusColor(value) || '#94a3b8';
    return (
      <span className={detailStyles.pill} style={{ background: `${color}1f`, color: '#334155' }}>
        <span className={detailStyles.pillDot} style={{ background: color }} />
        {value}
      </span>
    );
  };

  const renderLeadActionPill = (actionId) => {
    const action = leadActions.find(a => String(a.action_id) === String(actionId));
    if (!action) return null;
    const color = action.color || '#94a3b8';
    return (
      <span className={detailStyles.pill} style={{ background: `${color}1f`, color: '#334155' }}>
        <span className={detailStyles.pillDot} style={{ background: color }} />
        {action.name}
      </span>
    );
  };

  const handleInlineLeadStatusChange = async (newStatus) => {
    const applyChange = async () => {
      await doStatusChange(editableLead, newStatus);
      setEditableLead(prev => ({ ...prev, status: newStatus }));
      setActivityRefreshKey(k => k + 1);
    };

    if (newStatus === 'Won') {
      setStatusConfirmMessage('Are you sure you want to mark this lead as Won? It will be archived and a project will be created.');
      setStatusConfirmButton('Yes, Mark as Won');
      setPendingStatusAction(() => applyChange);
      setShowStatusConfirm(true);
      return;
    }

    if (newStatus === 'Lost') {
      setStatusConfirmMessage('Are you sure you want to mark this lead as Lost? It will be archived.');
      setStatusConfirmButton('Yes, Mark as Lost');
      setPendingStatusAction(() => applyChange);
      setShowStatusConfirm(true);
      return;
    }

    await applyChange();
  };

  const handleNameFocus = async (isEdit = false) => {
    setShowNameDropdown(true);
    // Show contacts based on selected company or all if no company selected
    const currentCompany = isEdit ? editableLead?.client_company : client_company;
    const currentName = isEdit ? editableLead?.client_name || '' : client_name;
    
    if (currentCompany) {
      // Check if we have contacts loaded for this company
      const company = allClientCompanies.find(c => c.company_name === currentCompany);
      if (company && (!clientContacts.length || clientContacts[0]?.company_name !== currentCompany)) {
        // Load contacts for this company if not already loaded
        const contacts = await getClientContacts(company.client_company_id);
        setClientContacts(contacts);
      }
      updateNameSuggestions(currentName, currentCompany);
    } else {
      // Load all contacts when no company is selected
      if (!clientContacts.length) {
        const allContacts = await getClientContacts(null);
        setClientContacts(allContacts);
      }
      updateNameSuggestions(currentName, null);
    }
  };

  const handleNameInput = (val, isEdit = false) => {
    if (isEdit) {
      setEditableLead({ ...editableLead, client_name: val });
    } else {
      setName(val);
    }
    const currentCompany = isEdit ? editableLead?.client_company : client_company;
    updateNameSuggestions(val, currentCompany);
  };

  const updateNameSuggestions = (query, companyName) => {
    let suggestions = [];
    let contactsToFilter = [];
    
    if (companyName && clientContacts.length > 0) {
      contactsToFilter = clientContacts;
    } else if (!companyName && clientContacts.length > 0) {
      contactsToFilter = clientContacts;
    }
    
    if (contactsToFilter.length > 0) {
      contactsToFilter = contactsToFilter.filter(contact => contact.name && contact.name.trim());
      
      if (query) {
        // Check if there's an exact match
        const exactMatch = contactsToFilter.find(contact => 
          contact.name.toLowerCase() === query.toLowerCase()
        );
        
        if (exactMatch) {
          // If exact match exists, only show that
          suggestions = [exactMatch.name];
        } else {
          // Otherwise, show all that start with the query
          suggestions = contactsToFilter
            .filter(contact => 
              contact.name.toLowerCase().startsWith(query.toLowerCase())
            )
            .map(contact => contact.name);
        }
      } else {
        // No query, show all
        suggestions = contactsToFilter.map(contact => contact.name);
      }
    }
    setNameSuggestions(suggestions);
  };

  const handleNameSelect = (contactName, isEdit = false) => {
    const contact = clientContacts.find(c => c.name === contactName);
    if (isEdit) {
      setEditableLead({ 
        ...editableLead, 
        client_name: contactName,
        email: contact ? contact.email || '' : editableLead.email,
        phone_number: contact ? contact.phone_number || '' : editableLead.phone_number
      });
    } else {
      setName(contactName);
      setEmail(contact ? contact.email || '' : '');
      setPhone(contact ? contact.phone_number || '' : '');
    }
    setNameSuggestions([]);
    setShowNameDropdown(false);
  };

  const resetForm = () => {
    setName("");
    setCompany("");
    setProject("");
    setEmail("");
    setPhone("");
    setDescription("");
    setSelectedLabels([]);
    setActionId("");
    setBudgetCost("");
    setBudgetHours("");
    setLocation("");
    setAddress("");
    setActiveTab("details");
    setCompanySuggestions([]);
    setNameSuggestions([]);
    setClientContacts([]);
    setShowCompanyDropdown(false);
    setShowNameDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Permission check before submitting
    if (!canCreateLeads) {
      setToast({ message: "You don't have permission to create leads.", type: "error" });
      return;
    }

    try {
      const result = await createLead({
        client_name,
        client_company,
        project_name,
        email,
        phone_number,
        description,
        budget_cost: budget_cost ? parseFloat(budget_cost) : null,
        budget_hours: budget_hours ? parseFloat(budget_hours) : null,
        location,
        address,
        action_id: actionId || null,
      });

      // Save labels for the new lead
      if (result.lead && selectedLabels.length > 0) {
        await setLeadLabels(
          result.lead.lead_id,
          selectedLabels.map(l => l.label_id)
        );
      }

      // Show any messages from contact/company resolution
      if (result.messages && result.messages.length > 0) {
        setToast({ message: result.messages.join(', '), type: 'info' });
      }

      setShowForm(false);
      resetForm();

      const updated = await getLeads();
      setLeads(updated.leads);
    } catch (err) {
      console.error(err);
      setToast({ message: err.message || "Failed to save lead. Try again.", type: "error" });
    }
  };

  const applyOptimisticLeadUpdate = (leadId, patch) => {
    let previous = null;
    setLeads((prev) => prev.map((l) => {
      if (l.lead_id !== leadId) return l;
      previous = l;
      return { ...l, ...patch };
    }));
    return previous;
  };

  const rollbackLead = (previous) => {
    if (!previous) return;
    setLeads((prev) => prev.map((l) => l.lead_id === previous.lead_id ? previous : l));
  };

  const doStatusChange = async (lead, newStatus) => {
    const previous = applyOptimisticLeadUpdate(lead.lead_id, { status: newStatus });
    try {
      const updated = await updateLead(lead.lead_id, { status: newStatus });
      if (updated && updated.lead) {
        if (newStatus === WON_STATUS) {
          await acceptLeadAndCreateProject(lead.lead_id);
        }
        setLeads((prev) =>
          prev.map((l) =>
            l.lead_id === lead.lead_id ? {
              ...updated.lead,
              action_name: l.action_name,
              action_color: l.action_color,
              labels: l.labels
            } : l
          )
        );
      }
    } catch (err) {
      console.error(err);
      rollbackLead(previous);
      setToast({ message: err.message || "Failed to update status.", type: "error" });
    }
  };

  const handleStatusChange = async (lead, newStatus) => {
    // Check permissions for editing
    if (!canEditLeads) {
      setToast({ message: "You don't have permission to edit leads.", type: "error" });
      return;
    }

    if (newStatus === 'Won') {
      setStatusConfirmMessage('Are you sure you want to mark this lead as Won? It will be archived and a project will be created.');
      setStatusConfirmButton('Yes, Mark as Won');
      setPendingStatusAction(() => async () => {
        await doStatusChange(lead, newStatus);
      });
      setShowStatusConfirm(true);
      return;
    }

    if (newStatus === 'Lost') {
      setStatusConfirmMessage('Are you sure you want to mark this lead as Lost? It will be archived.');
      setStatusConfirmButton('Yes, Mark as Lost');
      setPendingStatusAction(() => async () => {
        await doStatusChange(lead, newStatus);
      });
      setShowStatusConfirm(true);
      return;
    }

    await doStatusChange(lead, newStatus);
  };

  const handleRestoreLead = async (lead) => {
    if (!canEditLeads) {
      setToast({ message: "You don't have permission to edit leads.", type: "error" });
      return;
    }
    try {
      const updated = await updateLead(lead.lead_id, { status: 'Open' });
      if (updated && updated.lead) {
        setLeads((prev) =>
          prev.map((l) => l.lead_id === lead.lead_id ? { 
            ...updated.lead,
            action_name: l.action_name,
            action_color: l.action_color,
            labels: l.labels 
          } : l)
        );
        setToast({ message: "Lead restored successfully.", type: "success" });
      }
    } catch (err) {
      console.error(err);
      setToast({ message: err.message || "Failed to restore lead.", type: "error" });
    }
  };

  const handleActionChange = async (lead, newActionId) => {
    // Check permissions for editing
    if (!canEditLeads) {
      setToast({ message: "You don't have permission to edit leads.", type: "error" });
      return;
    }

    const action = leadActions.find(a => a.action_id === newActionId);
    const previous = applyOptimisticLeadUpdate(lead.lead_id, {
      action_id: newActionId || null,
      action_name: action?.name || null,
      action_color: action?.color || null,
    });

    try {
      const updated = await updateLead(lead.lead_id, { action_id: newActionId || null });
      if (updated && updated.lead) {
        setLeads((prev) =>
          prev.map((l) =>
            l.lead_id === lead.lead_id ? {
              ...updated.lead,
              action_name: action?.name || null,
              action_color: action?.color || null,
              labels: l.labels
            } : l
          )
        );
      }
    } catch (err) {
      console.error(err);
      rollbackLead(previous);
      setToast({ message: err.message || "Failed to update action.", type: "error" });
    }
  };

  const handleCreateAction = async ({ name, color, description }) => {
    const result = await createLeadAction({ name, color, description });
    const newAction = result.action;
    // Refresh actions list
    const actionsRes = await getLeadActions();
    setLeadActions(actionsRes.actions || []);
    // Auto-select the new action in the relevant context
    if (addActionContext === 'create') {
      setActionId(newAction.action_id);
    } else if (addActionContext === 'edit' && editableLead) {
      setEditableLead({ ...editableLead, action_id: newAction.action_id });
    }
    setShowAddActionModal(false);
    setAddActionContext(null);
    setToast({ message: `Action "${newAction.name}" created`, type: "success" });
  };

  const handleSaveActionOrder = async (orderedActionIds) => {
    try {
      setSavingActionOrder(true);
      const response = await reorderLeadActions(orderedActionIds);
      setLeadActions(response.actions || []);
      setShowActionReorderModal(false);
      setToast({ message: 'Lead action order updated', type: 'success' });
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Failed to reorder lead actions';
      setToast({ message: msg, type: 'error' });
    } finally {
      setSavingActionOrder(false);
    }
  };

  const handleExportSubmit = async (e) => {
    e.preventDefault();
    try {
      // Build query params for export
      const params = {};

      if (exportForm.status) params.status = exportForm.status;
      if (exportForm.includeLabels) params.include_labels = 'true';
      if (exportForm.includeBudget) params.include_budget = 'true';
      if (exportForm.includeCustomFields) params.include_custom_fields = 'true';
      if (exportForm.groupBy) params.group_by = exportForm.groupBy;

      // Call the export service
      await exportLeads(params);
      setToast({ message: 'Leads exported successfully', type: 'success' });
      setShowExportModal(false);
    } catch (error) {
      console.error("Export failed:", error);
      setToast({ message: error.message || "Failed to export leads", type: "error" });
    }
  };

  const formatCurrency = (value) => {
    if (!value) return "—";
    return formatMoney(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const detailsFields = [
    { label: "Project Name", value: project_name, onChange: (e) => setProject(e.target.value) },
    { 
      label: "Client Details", 
      type: "custom",
      render: () => (
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Client Name</label>
            <input
              type="text"
              value={client_name}
              onChange={(e) => handleNameInput(e.target.value)}
              onFocus={handleNameFocus}
              onBlur={() => setTimeout(() => setShowNameDropdown(false), 200)}
              style={{ width: '100%' }}
            />
            {nameSuggestions.length > 0 && showNameDropdown && (
              <ul style={{ position: 'absolute', zIndex: 10, background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', listStyle: 'none', padding: '4px 0', margin: '4px 0 0', width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' }}>
                {nameSuggestions.map((s, i) => (
                  <li key={i} onClick={() => handleNameSelect(s)} style={{ padding: '8px 12px', cursor: 'pointer', hover: { backgroundColor: '#f3f4f6' } }}>{s}</li>
                ))}
              </ul>
            )}
          </div>
          <div style={{ flex: 1, position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Company Name</label>
            <input
              type="text"
              value={client_company}
              onChange={(e) => handleCompanyInput(e.target.value)}
              onFocus={handleCompanyFocus}
              onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 200)}
              style={{ width: '100%' }}
            />
            {companySuggestions.length > 0 && showCompanyDropdown && (
              <ul style={{ position: 'absolute', zIndex: 10, background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', listStyle: 'none', padding: '4px 0', margin: '4px 0 0', width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' }}>
                {companySuggestions.map((s, i) => (
                  <li key={i} onClick={() => handleCompanySelect(s)} style={{ padding: '8px 12px', cursor: 'pointer', hover: { backgroundColor: '#f3f4f6' } }}>{s}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )
    },
    { 
      label: "Contact Info", 
      type: "custom",
      render: () => (
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Phone Number</label>
            <input
              type="tel"
              value={phone_number}
              onChange={(e) => setPhone(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      )
    },
    { 
      label: "Labels", 
      type: "custom",
      render: () => (
        <LabelSelector
          selectedLabels={selectedLabels}
          onChange={setSelectedLabels}
          placeholder="Add service type labels..."
        />
      )
    },
    { 
      label: "Action", 
      type: "custom",
      render: () => (
        <CustomDropdown
          options={leadActions.map(a => ({ value: a.action_id, label: a.name, color: a.color }))}
          value={actionId}
          onChange={(val) => setActionId(val)}
          placeholder="Select action..."
          fullWidth
          actions={[{ label: '+ Add Action', onClick: () => { setAddActionContext('create'); setShowAddActionModal(true); } }]}
        />
      )
    },
    { 
      label: "Location & Address", 
      type: "custom",
      render: () => (
        <div style={{ display: 'flex', gap: '16px', flexDirection: 'column' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Location</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g., New York, NY"
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Address</label>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Full street address"
              style={{ width: '100%', minHeight: '60px' }}
            />
          </div>
        </div>
      )
    },
    { label: "Description", value: description, onChange: (e) => setDescription(e.target.value), type: "textarea" },
  ];

  const budgetFields = [
    {
      label: `Estimated Budget (${currencySymbol})`,
      value: budget_cost,
      onChange: (e) => setBudgetCost(e.target.value),
      type: "number",
      step: "0.01",
      min: "0",
      placeholder: "0.00"
    },
    { 
      label: "Estimated Hours", 
      value: budget_hours, 
      onChange: (e) => setBudgetHours(e.target.value),
      type: "number",
      step: "0.5",
      min: "0",
      placeholder: "0"
    },
  ];

  // Permission check: Don't render page if user doesn't have view permission
  if (!canViewLeads && !canViewAssignedLeads) {
    return (
      <div className={ui.emptyState}>
        <h3>Access Denied</h3>
        <p>You don't have permission to view leads.</p>
      </div>
    );
  }

  return (
    <div className={`${ui.pageContainer} ${viewMode === 'kanban' ? ui.pageContainerGantt : ''}`}>
      {/* Page Header */}
      <div className={ui.pageHeader}>
        <div className={ui.pageHeaderLeft}>
          <h1 className={ui.pageTitle}>{showArchive ? 'Leads Archive' : 'Leads'}</h1>
          {showLoading ? (
            <span className={`${ui.skeletonBlock} ${ui.pageCountSkeleton}`} />
          ) : (
            <span className={ui.pageCount}>
              {displayedLeads.length} {displayedLeads.length === 1 ? 'lead' : 'leads'}
            </span>
          )}
        </div>
        <div className={ui.pageHeaderRight}>
          <div className={ui.viewToggleGroup} role="tablist" aria-label="View mode">
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'table'}
              className={`${ui.viewToggleButton} ${viewMode === 'table' ? ui.viewToggleButtonActive : ''}`}
              onClick={() => setViewMode('table')}
            >
              Table
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'kanban'}
              className={`${ui.viewToggleButton} ${viewMode === 'kanban' ? ui.viewToggleButtonActive : ''}`}
              onClick={() => setViewMode('kanban')}
            >
              Kanban
            </button>
          </div>
          {viewMode === 'table' && (
          <div style={{ position: "relative" }} ref={settingsRef}>
            <button
              className={ui.iconButton}
              onClick={() => setShowSettings((prev) => !prev)}
              title="Column Settings"
            >
              <img src={settingsIcon} alt="Settings" width="18" height="18" />
            </button>

            {showSettings && (
              <SettingsToggle
                columns={columns}
                toggleColumn={toggleColumn}
                refEl={settingsRef}
              />
            )}
          </div>
          )}
          
          <button
            className={`${ui.archiveToggle} ${showArchive ? ui.archiveToggleActive : ''}`}
            onClick={() => {
              setShowArchive((prev) => !prev);
              setFilterStatus("");
              setSearchTerm("");
            }}
            title={showArchive ? "View active leads" : "View archived leads"}
          >
            {showArchive ? '← Active' : 'Archive'}
            {!showArchive && archivedCount > 0 && (
              <span className={ui.archiveBadge}>{archivedCount}</span>
            )}
          </button>
          
          <button 
            className={ui.exportButton} 
            onClick={() => setShowExportModal(true)}
            title="Export to CSV"
          >
            Export
          </button>

          {canManageCompanySettings && leadActions.length > 1 && (
            <button
              className={ui.exportButton}
              onClick={() => setShowActionReorderModal(true)}
              title="Reorder actions"
            >
              Reorder Actions
            </button>
          )}
          
          {canCreateLeads && (
            <button className={ui.addButton} onClick={() => setShowForm(true)}>
              <img src={plusIcon} alt="" width="16" height="16" />
              Add Lead
            </button>
          )}
        </div>
      </div>

      {/* Toolbar: Search, Filter, Group */}
      {showLoading ? (
        <div className={ui.toolbarSkeleton}>
          {Array.from({ length: 6 }).map((_, idx) => (
            <div key={`leads-toolbar-skeleton-${idx}`} className={`${ui.skeletonBlock} ${ui.toolbarSkeletonPill}`} />
          ))}
        </div>
      ) : (
        <div className={ui.tableToolbar}>
          <input
            type="text"
            className={ui.toolbarSearch}
            placeholder="Search leads…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <CustomDropdown
            options={[
              { value: '', label: 'All Statuses' },
              ...(showArchive ? CLOSED_STATUSES : ACTIVE_STATUSES).map((s) => ({ value: s, label: s }))
            ]}
            value={filterStatus}
            onChange={(val) => setFilterStatus(val)}
            toolbar
          />
          <CustomDropdown
            options={[
              { value: '', label: 'All Actions' },
              ...leadActions.map((a) => ({ value: String(a.action_id), label: a.name, color: a.color }))
            ]}
            value={filterAction}
            onChange={(val) => setFilterAction(val)}
            toolbar
          />
          <CustomDropdown
            options={[
              { value: '', label: 'All Labels' },
              ...availableLabels.map(([id, name]) => ({ value: String(id), label: name }))
            ]}
            value={filterLabel}
            onChange={(val) => setFilterLabel(val)}
            toolbar
          />
          {viewMode === 'table' && (
            <CustomDropdown
              options={[
                { value: '', label: 'No Grouping' },
                { value: 'status', label: 'Group by Status' },
                { value: 'action_name', label: 'Group by Action' },
                { value: 'client_company', label: 'Group by Company' },
              ]}
              value={groupBy}
              onChange={(val) => setGroupBy(val)}
              toolbar
            />
          )}
          {viewMode === 'table' && (
            <CustomDropdown
              options={[
                { value: 'action', label: 'Color: Action' },
                { value: 'status', label: 'Color: Status' },
              ]}
              value={colorDotMode}
              onChange={(val) => setColorDotMode(val)}
              toolbar
            />
          )}
          {viewMode === 'kanban' && (
            <CustomDropdown
              options={[
                { value: 'status', label: 'Group by Status' },
                { value: 'action', label: 'Group by Action' },
              ]}
              value={kanbanGroupBy}
              onChange={(val) => setKanbanGroupBy(val)}
              toolbar
            />
          )}
          <button
            className={ui.toolbarReset}
            onClick={resetView}
            title="Reset to default view"
          >
            Clear
          </button>
        </div>
      )}

      {/* New Lead Form with Tabs */}
      {showForm && canCreateLeads && (
        <FormModal
          title="New Lead"
          onCancel={() => {
            setShowForm(false);
            resetForm();
          }}
          onSubmit={handleSubmit}
          tabs={[
            { key: "details", label: "Details" },
            { key: "budget", label: "Budget" }
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          fields={activeTab === "details" ? detailsFields : budgetFields}
        />
      )}

      {/* Lead Details Modal (Read-only view) */}
      {showDetailModal && editableLead && (
        <DetailsModal
          title="Lead Details"
          onClose={() => {
            setShowDetailModal(false);
            setEditableLead(null);
            setEditLabels([]);
            setAssignedEmployees([]);
            setTeamDirty(false);
            setActiveTab("details");
          }}
          accentColor={getStatusColor(editableLead.status) || '#94a3b8'}
          menuActions={canDeleteLeads ? [{
            label: "Delete lead",
            danger: true,
            onClick: async () => {
              const confirmDelete = window.confirm("Are you sure you want to delete this lead?");
              if (confirmDelete) {
                try {
                  await deleteLead(editableLead.lead_id);
                  setShowDetailModal(false);
                  setEditableLead(null);
                  const updated = await getLeads();
                  setLeads(updated.leads);
                } catch (err) {
                  console.error("Failed to delete lead", err);
                  setToast({ message: err.message || "Failed to delete lead", type: "error" });
                }
              }
            },
          }] : []}
          titleBar={
            <>
              <h3 className={detailStyles.recordTitle}>
                <EditableField
                  bare
                  value={editableLead.project_name}
                  disabled={!canEditLeads}
                  placeholder="Untitled lead"
                  onSave={async (val) => {
                    await updateLeadAndRefresh(editableLead.lead_id, { project_name: val });
                    setEditableLead(prev => ({ ...prev, project_name: val }));
                    applyOptimisticLeadUpdate(editableLead.lead_id, { project_name: val });
                  }}
                />
              </h3>
              <div className={detailStyles.recordMeta}>
                <span className={detailStyles.recordMetaClient}>
                  <Icon name="building" size="0.85rem" className={detailStyles.recordMetaIcon} />
                  {editableLead.client_company || editableLead.client_name || 'No client'}
                </span>
                <span className={detailStyles.metaSeparator}>·</span>
                <EditableField
                  bare
                  inline
                  type="select"
                  value={editableLead.status || 'Open'}
                  options={LEAD_STATUSES.map(s => ({ value: s, label: s, color: getStatusColor(s) }))}
                  disabled={!canEditLeads}
                  formatDisplay={() => renderLeadStatusPill(editableLead.status)}
                  onSave={handleInlineLeadStatusChange}
                />
              </div>
            </>
          }
          tabs={[
            { key: "details", label: "Details" },
            { key: "budget", label: "Budget" },
            { key: "team", label: "Team" },
            { key: "files", label: "Files" },
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          sidePanel={
            <ActivityPanel
              entityType="lead"
              entityId={editableLead.lead_id}
              canComment={canViewLeads || canViewAssignedLeads}
              refreshSignal={activityRefreshKey}
              onFileUploaded={async () => {
                try {
                  const files = await activityService.listFiles('lead', editableLead.lead_id);
                  setLeadFiles(files || []);
                } catch (err) {
                  console.error("Failed to refresh lead files:", err);
                }
              }}
            />
          }
        >
          {activeTab === "details" && (
            <div>
              <div className={detailStyles.group}>
                <h4 className={detailStyles.groupTitle}>
                  <Icon name="status" size="0.9rem" className={detailStyles.groupTitleIcon} />
                  Properties
                </h4>

                <EditableField
                  label="Status"
                  icon="status"
                  type="select"
                  value={editableLead.status || 'Open'}
                  options={LEAD_STATUSES.map(s => ({ value: s, label: s, color: getStatusColor(s) }))}
                  disabled={!canEditLeads}
                  formatDisplay={() => renderLeadStatusPill(editableLead.status)}
                  onSave={handleInlineLeadStatusChange}
                />

                <EditableField
                  label="Action"
                  icon="flag"
                  type="select"
                  value={String(editableLead.action_id || '')}
                  options={leadActions.map(a => ({ value: String(a.action_id), label: a.name, color: a.color }))}
                  disabled={!canEditLeads}
                  placeholder="No action set"
                  formatDisplay={() => renderLeadActionPill(editableLead.action_id)}
                  onSave={async (val) => {
                    const actionId = val ? Number(val) : null;
                    await handleActionChange(editableLead, actionId);
                    setEditableLead(prev => ({ ...prev, action_id: actionId }));
                  }}
                />

                <div className={detailStyles.propRow}>
                  <span className={detailStyles.propLabel}>
                    <Icon name="tag" size="0.95rem" className={detailStyles.propIcon} />
                    Labels
                  </span>
                  <div className={detailStyles.propValue}>
                    {editingLabels ? (
                      <>
                        <LabelSelector
                          selectedLabels={editLabels}
                          onChange={async (labels) => {
                            setEditLabels(labels);
                            try {
                              await setLeadLabels(editableLead.lead_id, labels.map(l => l.label_id));
                            } catch (err) {
                              setToast({ message: err.message || "Failed to update labels", type: "error" });
                            }
                          }}
                          placeholder="Add service type labels..."
                        />
                        <button
                          type="button"
                          className={ui.cancelButton}
                          onClick={() => setEditingLabels(false)}
                        >
                          Done
                        </button>
                      </>
                    ) : editLabels.length > 0 ? (
                      <div
                        className={detailStyles.labelsList}
                        onClick={() => canEditLeads && setEditingLabels(true)}
                        style={{ cursor: canEditLeads ? 'pointer' : 'default' }}
                      >
                        {editLabels.map(label => (
                          <span
                            key={label.label_id}
                            className={detailStyles.labelTag}
                            style={{ backgroundColor: label.color || '#6b7280' }}
                          >
                            {label.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={detailStyles.addInlineButton}
                        disabled={!canEditLeads}
                        onClick={() => setEditingLabels(true)}
                      >
                        <Icon name="plus" size="0.8rem" /> Add label
                      </button>
                    )}
                  </div>
                </div>

                <EditableField
                  label="Location"
                  icon="mapPin"
                  value={editableLead.location}
                  disabled={!canEditLeads}
                  placeholder="Add a location"
                  onSave={async (val) => {
                    await updateLeadAndRefresh(editableLead.lead_id, { location: val });
                    setEditableLead(prev => ({ ...prev, location: val }));
                  }}
                />

                <EditableField
                  label="Address"
                  icon="building"
                  type="textarea"
                  value={editableLead.address}
                  disabled={!canEditLeads}
                  placeholder="Add an address"
                  onSave={async (val) => {
                    await updateLeadAndRefresh(editableLead.lead_id, { address: val });
                    setEditableLead(prev => ({ ...prev, address: val }));
                  }}
                />

                <CustomFieldRows
                  fields={customFields}
                  entity={editableLead}
                  disabled={!canEditLeads}
                  formatMoney={formatMoney}
                  onSave={async (field, val) => {
                    await updateLeadAndRefresh(editableLead.lead_id, {
                      custom_fields: { [field.field_id]: val },
                    });
                    const merged = { ...(editableLead.custom_fields || {}), [field.field_id]: val };
                    setEditableLead(prev => ({ ...prev, custom_fields: merged }));
                    // Custom fields are table columns too, so patch the row
                    // behind the modal rather than waiting for a refetch.
                    applyOptimisticLeadUpdate(editableLead.lead_id, { custom_fields: merged });
                  }}
                />
              </div>

              <div className={detailStyles.group}>
                <h4 className={detailStyles.groupTitle}>
                  <Icon name="text" size="0.9rem" className={detailStyles.groupTitleIcon} />
                  Description
                </h4>
                <EditableField
                  bare
                  flush
                  type="textarea"
                  value={editableLead.description}
                  disabled={!canEditLeads}
                  placeholder="Add a description…"
                  onSave={async (val) => {
                    await updateLeadAndRefresh(editableLead.lead_id, { description: val });
                    setEditableLead(prev => ({ ...prev, description: val }));
                  }}
                />
              </div>

              <div className={detailStyles.group}>
                <h4 className={detailStyles.groupTitle}>
                  <Icon name="user" size="0.9rem" className={detailStyles.groupTitleIcon} />
                  Client
                </h4>

                <EditableField
                  label="Contact name"
                  icon="user"
                  value={editableLead.client_name}
                  disabled={!canEditLeads}
                  placeholder="Add a contact"
                  onSave={async (val) => {
                    await updateLeadAndRefresh(editableLead.lead_id, {
                      client_name: val,
                      client_company: editableLead.client_company,
                      email: editableLead.email,
                      phone_number: editableLead.phone_number,
                    });
                    setEditableLead(prev => ({ ...prev, client_name: val }));
                  }}
                />

                <EditableField
                  label="Company"
                  icon="building"
                  value={editableLead.client_company}
                  disabled={!canEditLeads}
                  placeholder="Add a company"
                  onSave={async (val) => {
                    await updateLeadAndRefresh(editableLead.lead_id, {
                      client_name: editableLead.client_name,
                      client_company: val,
                      email: editableLead.email,
                      phone_number: editableLead.phone_number,
                    });
                    setEditableLead(prev => ({ ...prev, client_company: val }));
                  }}
                />

                <EditableField
                  label="Email"
                  icon="mail"
                  value={editableLead.email}
                  disabled={!canEditLeads}
                  placeholder="Add an email"
                  formatDisplay={(val) => val ? (
                    <a
                      href={`mailto:${val}`}
                      className={detailStyles.inlineLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {val}
                    </a>
                  ) : null}
                  onSave={async (val) => {
                    await updateLeadAndRefresh(editableLead.lead_id, {
                      client_name: editableLead.client_name,
                      client_company: editableLead.client_company,
                      email: val,
                      phone_number: editableLead.phone_number,
                    });
                    setEditableLead(prev => ({ ...prev, email: val }));
                  }}
                />

                <EditableField
                  label="Phone"
                  icon="phone"
                  value={editableLead.phone_number}
                  disabled={!canEditLeads}
                  placeholder="Add a phone number"
                  onSave={async (val) => {
                    await updateLeadAndRefresh(editableLead.lead_id, {
                      client_name: editableLead.client_name,
                      client_company: editableLead.client_company,
                      email: editableLead.email,
                      phone_number: val,
                    });
                    setEditableLead(prev => ({ ...prev, phone_number: val }));
                  }}
                />
              </div>
            </div>
          )}

          {activeTab === "budget" && (
            <div className={detailStyles.group}>
              <h4 className={detailStyles.groupTitle}>
                <Icon name="wallet" size="0.9rem" className={detailStyles.groupTitleIcon} />
                Budget estimate
              </h4>

              <div className={detailStyles.statGrid}>
                <div className={detailStyles.statCard}>
                  <div className={detailStyles.statLabel}>
                    <Icon name="wallet" size="0.85rem" />
                    Estimated budget
                  </div>
                  <div className={detailStyles.statValue}>
                    <EditableField
                      bare
                      type="number"
                      value={editableLead.budget_cost}
                      disabled={!canEditLeads}
                      placeholder="Set a budget"
                      formatDisplay={(val) => val ? formatCurrency(val) : null}
                      onSave={async (val) => {
                        const cost = val ? parseFloat(val) : 0;
                        await updateLeadAndRefresh(editableLead.lead_id, { budget_cost: cost });
                        setEditableLead(prev => ({ ...prev, budget_cost: cost }));
                      }}
                    />
                  </div>
                </div>
                <div className={detailStyles.statCard}>
                  <div className={detailStyles.statLabel}>
                    <Icon name="clock" size="0.85rem" />
                    Estimated hours
                  </div>
                  <div className={detailStyles.statValue}>
                    <EditableField
                      bare
                      type="number"
                      value={editableLead.budget_hours}
                      disabled={!canEditLeads}
                      placeholder="Set hours"
                      formatDisplay={(val) => val ? `${val} hrs` : null}
                      onSave={async (val) => {
                        const hours = val ? parseFloat(val) : 0;
                        await updateLeadAndRefresh(editableLead.lead_id, { budget_hours: hours });
                        setEditableLead(prev => ({ ...prev, budget_hours: hours }));
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "team" && (
            <div className={detailStyles.group}>
              <h4 className={detailStyles.groupTitle}>
                <Icon name="user" size="0.9rem" className={detailStyles.groupTitleIcon} />
                Team members
                {assignedEmployees.length > 0 && (
                  <span className={detailStyles.groupCount}>{assignedEmployees.length}</span>
                )}
              </h4>

              {canManageLeadTeam && (
                <div className={detailStyles.inlineSearch}>
                  <input
                    type="text"
                    className={detailStyles.searchInput}
                    placeholder="Search people by name or job title…"
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    onFocus={() => setTeamSearchFocused(true)}
                    onBlur={() => setTimeout(() => setTeamSearchFocused(false), 200)}
                  />
                  {teamSearchFocused && (
                    <div className={detailStyles.searchResults}>
                      {availableEmployees
                        .filter(emp => (emp.name + emp.job_title).toLowerCase().includes(employeeSearch.toLowerCase()))
                        .filter(emp => !assignedEmployees.some(a => a.id === emp.id))
                        .map(emp => (
                          <button
                            key={emp.id}
                            type="button"
                            className={detailStyles.searchResult}
                            onClick={() => handleAddLeadTeamMember(emp)}
                          >
                            {emp.name}
                            <span className={detailStyles.searchResultMeta}> — {emp.job_title}</span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              )}

              {assignedEmployees.length > 0 ? (
                <div>
                  {assignedEmployees.map(emp => (
                    <div key={emp.id} className={detailStyles.listItem}>
                      <Avatar name={emp.name} imageUrl={emp.profile_picture} size="sm" />
                      <div className={detailStyles.listItemBody}>
                        <div className={detailStyles.listItemTitle}>{emp.name}</div>
                        <div className={detailStyles.listItemMeta}>
                          {emp.job_title || 'Team member'}
                        </div>
                      </div>
                      {canManageLeadTeam && (
                        <button
                          type="button"
                          className={detailStyles.iconButton}
                          onClick={() => handleRemoveLeadTeamMember(emp.id)}
                          aria-label={`Remove ${emp.name}`}
                          title="Remove from lead"
                        >
                          <Icon name="close" size="0.9rem" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className={detailStyles.emptyMessage}>No team members assigned yet.</p>
              )}
            </div>
          )}

          {activeTab === "files" && (
            <div className={detailStyles.group}>
              <h4 className={detailStyles.groupTitle}>
                <Icon name="file" size="0.9rem" className={detailStyles.groupTitleIcon} />
                Files
                {leadFiles.length > 0 && (
                  <span className={detailStyles.groupCount}>{leadFiles.length}</span>
                )}
                {canEditLeads && (
                  <button
                    type="button"
                    className={detailStyles.groupAction}
                    onClick={() => leadFileInputRef.current?.click()}
                    disabled={uploadingLeadFile || !FILE_UPLOADS_ENABLED}
                    title={FILE_UPLOADS_ENABLED ? undefined : UPLOADS_DISABLED_MESSAGE}
                  >
                    <Icon name="upload" size="0.8rem" />
                    {uploadingLeadFile ? "Uploading…" : "Upload file"}
                  </button>
                )}
              </h4>

              <input
                ref={leadFileInputRef}
                type="file"
                className={detailStyles.hiddenFileInput}
                onChange={handleLeadFileUpload}
              />

              {leadFiles.length > 0 ? (
                <div>
                  {leadFiles.map(file => {
                    const canDelete = file.uploaded_by_user_id === currentUser?.user_id || canEditLeads;
                    return (
                      <div key={file.file_id} className={detailStyles.listItem}>
                        <Icon name="file" size="1.1rem" className={detailStyles.fileIcon} />
                        <div className={detailStyles.listItemBody}>
                          <button
                            type="button"
                            onClick={() => activityService.openAttachment("lead", editableLead.lead_id, file.file_id)}
                            className={detailStyles.listItemTitle}
                          >
                            {file.original_name}
                          </button>
                          <div className={detailStyles.listItemMeta}>
                            {file.uploaded_by_name || 'Unknown'} · {new Date(file.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        {canDelete && (
                          <button
                            type="button"
                            className={detailStyles.iconButton}
                            aria-label={`Delete ${file.original_name}`}
                            title="Delete file"
                            onClick={() => handleLeadFileDelete(file)}
                          >
                            <Icon name="trash" size="0.9rem" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className={detailStyles.emptyMessage}>
                  {FILE_UPLOADS_ENABLED
                    ? "No files yet — upload one to get started."
                    : "No files yet. Uploads are disabled until cloud storage is set up."}
                </p>
              )}
            </div>
          )}
        </DetailsModal>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <FormModal
          title="Export Leads to CSV"
          onCancel={() => setShowExportModal(false)}
          onSubmit={handleExportSubmit}
          fields={[
            {
              label: "Status (optional)",
              type: "select",
              value: exportForm.status,
              onChange: (e) =>
                setExportForm((prev) => ({ ...prev, status: e.target.value })),
              options: [
                { value: "", label: "All statuses" },
                ...LEAD_STATUSES.map(status => ({
                  value: status,
                  label: status
                }))
              ],
            },
            {
              label: "Group By (optional)",
              type: "select",
              value: exportForm.groupBy,
              onChange: (e) =>
                setExportForm((prev) => ({ ...prev, groupBy: e.target.value })),
              options: [
                { value: "", label: "No grouping" },
                { value: "company", label: "Client Company" },
                { value: "contact", label: "Client Name" }
              ],
            },
            {
              type: "custom",
              render: () => (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={exportForm.includeLabels}
                    onChange={(e) =>
                      setExportForm((prev) => ({ ...prev, includeLabels: e.target.checked }))
                    }
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: '#3a6aed',
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#334155' }}>Include labels in export</span>
                </label>
              ),
            },
            {
              type: "custom",
              render: () => (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={exportForm.includeBudget}
                    onChange={(e) =>
                      setExportForm((prev) => ({ ...prev, includeBudget: e.target.checked }))
                    }
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: '#3a6aed',
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#334155' }}>Include budget information in export</span>
                </label>
              ),
            },
            ...(customFields.length > 0 ? [{
              type: "custom",
              render: () => (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={exportForm.includeCustomFields}
                    onChange={(e) =>
                      setExportForm((prev) => ({ ...prev, includeCustomFields: e.target.checked }))
                    }
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: '#3a6aed',
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#334155' }}>Include custom fields in export</span>
                </label>
              ),
            }] : []),
          ]}
        />
      )}

      {/* Table */}
      <div className={ui.dataList}>
        {showLoading ? (
          <div className={ui.tableSkeleton}>
            <div className={`${ui.skeletonBlock} ${ui.tableSkeletonHeader}`} />
            {Array.from({ length: 6 }).map((_, idx) => (
              <div key={`leads-skeleton-row-${idx}`} className={`${ui.skeletonBlock} ${ui.tableSkeletonRow}`} />
            ))}
          </div>
        ) : displayedLeads.length === 0 ? (
          <div className={`${ui.emptyState} ${ui.fadeInContent}`}>
            <img
              src="/leads_empty.svg"
              alt="No leads"
              className={ui.emptyStateImage}
            />
            <h3>{showArchive ? (hasActiveFilters ? 'No matching archived leads' : 'No archived leads') : (hasActiveFilters ? 'No matching leads' : 'No leads yet')}</h3>
            <p>{showArchive ? (hasActiveFilters ? 'Try adjusting your search or filters' : 'Leads with Won or Lost status will appear here') : (hasActiveFilters ? 'Try adjusting your search or filters' : 'Create your first lead to start tracking potential projects')}</p>
            {canCreateLeads && !hasActiveFilters && !showArchive && (
              <button 
                className={ui.addButton} 
                onClick={() => setShowForm(true)}
              >
                <img src={plusIcon} alt="" width="16" height="16" />
                Add Lead
              </button>
            )}
          </div>
        ) : viewMode === 'kanban' ? (
          <div className={ui.fadeInContent} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <KanbanView
              columns={
                kanbanGroupBy === 'action'
                  ? leadActions.map(a => ({ id: a.action_id, name: a.name, color: a.color }))
                  : (showArchive ? CLOSED_STATUSES : ACTIVE_STATUSES).map(s => ({
                      id: s,
                      name: s,
                      color: getStatusColor(s),
                    }))
              }
              items={displayedLeads}
              getColumnId={(lead) => kanbanGroupBy === 'action' ? lead.action_id : lead.status}
              canEdit={canEditLeads}
              onCardClick={openLeadDetail}
              onCardMove={async (lead, newColumnId) => {
                if (kanbanGroupBy === 'action') {
                  await handleActionChange(lead, newColumnId);
                } else {
                  await handleStatusChange(lead, newColumnId);
                }
              }}
              renderCard={(lead) => {
                const actionColor = lead.action_color || '#a1a1aa';
                const statusColor = getStatusColor(lead.status);
                const labels = lead.labels || [];
                const visibleLabels = labels.slice(0, 3);
                const extraLabels = labels.length - visibleLabels.length;
                return (
                  <>
                    {labels.length > 0 && (
                      <div className={kanban.cardHeader}>
                        {visibleLabels.map(lb => (
                          <span key={lb.label_id} className={kanban.cardLabelTag} title={lb.name}>
                            <span className={kanban.cardLabelDot} style={{ background: lb.color || '#a1a1aa' }} />
                            {lb.name}
                          </span>
                        ))}
                        {extraLabels > 0 && (
                          <span className={kanban.cardLabelExtra}>+{extraLabels}</span>
                        )}
                      </div>
                    )}
                    <p className={kanban.cardTitle}>{lead.project_name || '—'}</p>
                    {lead.client_company && (
                      <p className={kanban.cardSubtitle}>{lead.client_company}</p>
                    )}
                    <div className={kanban.cardFooter}>
                      <div className={kanban.cardFooterLeft}>
                        {kanbanGroupBy === 'status' && lead.action_name && (
                          <span className={kanban.cardBadge} style={{ background: '#f4f4f5', color: '#52525b' }}>
                            <span className={kanban.cardBadgeDot} style={{ background: actionColor }} />
                            {lead.action_name}
                          </span>
                        )}
                        {kanbanGroupBy === 'action' && (
                          <span className={kanban.cardBadge} style={{ background: '#f4f4f5', color: '#52525b' }}>
                            <span className={kanban.cardBadgeDot} style={{ background: statusColor }} />
                            {lead.status}
                          </span>
                        )}
                        {lead.budget_cost && (
                          <span className={kanban.cardDate}>
                            {formatMoney(lead.budget_cost, { maximumFractionDigits: 0, minimumFractionDigits: 0 })}
                          </span>
                        )}
                      </div>
                      {lead.assignees && lead.assignees.length > 0 && (
                        <div className={kanban.cardAvatars}>
                          {lead.assignees.slice(0, 3).map(a => (
                            <span
                              key={a.employee_id}
                              className={kanban.cardAvatar}
                              title={a.user_name}
                              style={{ background: avatarColorFromName(a.user_name) }}
                            >
                              {getInitials(a.user_name)}
                            </span>
                          ))}
                          {(lead.assignee_count || lead.assignees.length) > 3 && (
                            <span className={`${kanban.cardAvatar} ${kanban.cardAvatarMore}`} title={`${(lead.assignee_count || lead.assignees.length) - 3} more`}>
                              +{(lead.assignee_count || lead.assignees.length) - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                );
              }}
            />
          </div>
        ) : (
          <div className={ui.fadeInContent}>
            <DataTable
              columns={columns}
              data={displayedLeadsWithColor}
              groupedBy={groupBy || null}
              collapsedGroups={collapsedGroups}
              toggleGroupCollapse={toggleGroupCollapse}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              onRowClick={openLeadDetail}
              onAddColumn={canManageCompanySettings ? () => setShowAddFieldModal(true) : null}
              customCellRender={{
                status: (lead) => showArchive ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{lead.status}</span>
                    {canEditLeads && (
                      <button
                        className={ui.restoreButton}
                        onClick={(e) => { e.stopPropagation(); handleRestoreLead(lead); }}
                      >
                        Restore
                      </button>
                    )}
                  </div>
                ) : canEditLeads ? (
                  <CustomDropdown
                    sections={[
                      { items: ACTIVE_STATUSES.map(s => ({ value: s, label: s })) },
                      { items: [{ value: 'Won', label: 'Won' }] },
                      { items: [{ value: 'Lost', label: 'Lost' }] },
                    ]}
                    value={lead.status}
                    onChange={(val) => handleStatusChange(lead, val)}
                    compact
                  />
                ) : (
                  <span>{lead.status}</span>
                ),
                action: (lead) => canEditLeads ? (
                  <CustomDropdown
                    options={leadActions.map(a => ({ value: a.action_id, label: a.name, color: a.color }))}
                    value={lead.action_id || ''}
                    onChange={(val) => handleActionChange(lead, val)}
                    compact
                    placeholder="—"
                    actions={[{ label: '+ Add Action', onClick: () => { setAddActionContext('table'); setShowAddActionModal(true); } }]}
                  />
                ) : (
                  <span>{lead.action_name || '—'}</span>
                ),
                budget_cost: (lead) => formatCurrency(lead.budget_cost),
                labels: (lead) => {
                  if (lead.labels && lead.labels.length > 0) {
                    return (
                      <div className={detailStyles.labelsList}>
                        {lead.labels.map(label => (
                          <span 
                            key={label.label_id} 
                            className={detailStyles.labelTag}
                            style={{ backgroundColor: label.color || '#6b7280' }}
                          >
                            {label.name}
                          </span>
                        ))}
                      </div>
                    );
                  }
                  return "—";
                },
                ...customCellRenderers,
              }}
            />
          </div>
        )}
      </div>

      {/* Status Confirmation Dialog (Won/Lost) */}
      {showStatusConfirm && (
        <ConfirmDialog
          message={statusConfirmMessage}
          confirmText={statusConfirmButton}
          cancelText="Cancel"
          onConfirm={async () => {
            setShowStatusConfirm(false);
            if (pendingStatusAction) {
              await pendingStatusAction();
              setPendingStatusAction(null);
            }
          }}
          onCancel={() => {
            setShowStatusConfirm(false);
            setPendingStatusAction(null);
          }}
        />
      )}

      {/* Add Action Modal */}
      {showAddActionModal && (
        <AddStatusModal
          onSubmit={handleCreateAction}
          onCancel={() => { setShowAddActionModal(false); setAddActionContext(null); }}
        />
      )}

      {showAddFieldModal && (
        <CustomFieldModal
          defaultEntity="lead"
          onClose={() => setShowAddFieldModal(false)}
          onSaved={refreshCustomFields}
          onMessage={setToast}
        />
      )}

      <ReorderListModal
        isOpen={showActionReorderModal}
        title="Reorder Lead Actions"
        description="Drag actions to control how they appear on leads pages and filters."
        items={leadActions}
        getId={(item) => item.action_id}
        getLabel={(item) => item.name}
        getColor={(item) => item.color}
        onClose={() => setShowActionReorderModal(false)}
        onSave={handleSaveActionOrder}
        saving={savingActionOrder}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
